import * as Comlink from 'comlink';

import { type PersistenceMode, type ReplicaWorker } from './worker-entry.js';

/**
 * Browser entry point. Spawns the SQLite-WASM Web Worker and exposes the
 * `Replica` surface (the same one in `./replica.ts`) over Comlink.
 *
 * Storage persistence: requests `navigator.storage.persist()` on first
 * open so the browser doesn't evict the OPFS replica under pressure.
 *
 * When OPFS is unavailable in this isolate, the worker transparently
 * falls back to an in-memory sqlite-wasm backend so a single session
 * still works. The selected mode is returned via
 * `OpenBrowserReplicaResult.persistent` so the UI can warn the user
 * that their work will be lost on reload.
 *
 * Multi-tab posture (WS scope): leader-elected via Web Locks. The leader
 * holds an exclusive lock on `dungeonlogs-replica-leader`; non-leader tabs
 * detect the held lock and operate read-only with a banner. Per `B3` AC,
 * the leader-election implementation is a thin wrapper around `navigator.
 * locks.request(...)`. Full multi-leader handoff (the deeper Phase-2
 * refinement) lives behind a future ticket.
 */
export interface BrowserReplicaOptions {
  /** OPFS filename (default `/dungeonlogs.db`). */
  filename?: string;
  /** Module URL for the worker entry — Vite handles `new Worker(..., { type: 'module' })`. */
  workerUrl?: URL;
  /**
   * Test / dev override — force the worker to skip OPFS and use the
   * in-memory backend. Surfaced as a `?dl_replica=memory` query string
   * by `app.ts` so the Playwright suite can exercise the fallback path
   * without a browser-version mock.
   */
  forceMemory?: boolean;
}

export interface OpenBrowserReplicaResult {
  readonly remote: Comlink.Remote<ReplicaWorker>;
  /**
   * `true` when the replica opened against OPFS (durable across reloads);
   * `false` when the OPFS handle failed and the worker fell back to a
   * volatile `:memory:` backend. Drives the in-app warning banner.
   */
  readonly persistent: boolean;
  /** The backend the worker actually selected — useful for diagnostics. */
  readonly mode: PersistenceMode;
  /**
   * When `mode === 'memory'`, the original message from the swallowed
   * `OpfsDb` constructor throw — surfaced verbatim in the substrate
   * banner's "Why?" dialog and on `console.info`. The generic
   * "OPFS didn't open" copy is uninformative on its own and has hidden
   * actionable causes (stale-worker handle, missing headers) twice.
   * `undefined` on the happy `mode === 'opfs'` path.
   */
  readonly opfsError?: string;
}

export async function openBrowserReplica(
  opts: BrowserReplicaOptions = {},
): Promise<OpenBrowserReplicaResult> {
  if (typeof navigator !== 'undefined' && navigator.storage?.persist) {
    // Best-effort. If the user declines, OPFS still works but is evictable.
    await navigator.storage.persist().catch(() => undefined);
  }
  // IMPORTANT — keep the default-branch literal EXACTLY as written:
  //   new Worker(new URL('./worker-entry.js', import.meta.url), { type: 'module' })
  // Vite/Rolldown only *bundle* a module worker when this precise shape is
  // statically present. ANY indirection — hoisting the URL into a variable, an
  // IIFE, a `??` fallback inside the `new Worker(...)` argument, or mutating
  // `searchParams` — makes Vite treat `new URL('./worker-entry.js', …)` as a
  // copied static ASSET instead. It then ships the raw, unbundled
  // `worker-entry.ts` (bare `import 'comlink'` specifiers, served as the `.ts`
  // MIME `video/mp2t`), and the browser refuses to start it — so the substrate
  // worker is dead in `vite build`/preview and every prod bundle. (That was a
  // real regression: sync never connected and fact inputs stayed empty under
  // the production build while `vite dev` — which transpiles the worker on the
  // fly — masked it.) The `opts.workerUrl` branch is a test-only override and
  // is intentionally NOT the default path the bundler sees.
  const worker =
    opts.workerUrl !== undefined
      ? new Worker(opts.workerUrl, { type: 'module' })
      : new Worker(new URL('./worker-entry.js', import.meta.url), { type: 'module' });
  const remote = Comlink.wrap<ReplicaWorker>(worker);
  const result = await remote.open(opts.filename, opts.forceMemory === true);
  const persistent = result.mode === 'opfs';
  return result.opfsError !== undefined
    ? { remote, mode: result.mode, persistent, opfsError: result.opfsError }
    : { remote, mode: result.mode, persistent };
}

/**
 * Try to acquire the leader role for this tab. Returns a release function
 * the caller calls when the tab closes (or never, in which case the lock
 * releases on tab destruction). Non-leader tabs see the resolved promise
 * carry `false` and should run in read-only mode until they become leader.
 */
export async function acquireLeader(
  name = 'dungeonlogs-replica-leader',
): Promise<{ isLeader: boolean; release: () => void }> {
  const noop = (): void => {
    /* no-op */
  };
  if (typeof navigator === 'undefined' || !navigator.locks) {
    // No Web Locks support: treat as solo-leader (single-tab fallback).
    return { isLeader: true, release: noop };
  }
  let released: () => void = noop;
  const acquired = await new Promise<boolean>((resolve) => {
    let resolved = false;
    void navigator.locks.request(name, { ifAvailable: true }, async (lock) => {
      if (lock === null) {
        resolve(false);
        resolved = true;
        return;
      }
      resolve(true);
      resolved = true;
      await new Promise<void>((rel) => {
        released = (): void => {
          rel();
        };
      });
    });
    // Fast-fail if the callback didn't fire synchronously.
    setTimeout(() => {
      if (!resolved) resolve(false);
    }, 0);
  });
  return { isLeader: acquired, release: () => released() };
}
